import cs from 'classnames';
import { useEffect, useRef } from 'react';

import './hs.scss';

/**
 * - globe: 점과 선으로 엮은 지구본이 천천히 돈다. 끌면 따라 돌고(관성), 누르면 그 자리에서 파동이 퍼진다(로그인).
 * - net: 떠다니는 점들이 가까우면 선으로 이어진다. 손가락·마우스 쪽으로 모이고, 누르면 밀려난다(회원가입·안내 화면).
 */
export type HsBackdropVariant = 'globe' | 'net';

interface Props {
  variant: HsBackdropVariant;
  /** 점을 줄이고 옅게 그린다(404 등 안내 화면) */
  subtle?: boolean;
  className?: string;
}

type Rgb = [number, number, number];

interface Palette {
  accent: Rgb;
  /** 로고 그라데이션의 반대쪽 색(파랑). 지구본 색을 이 둘 사이로 섞는다 */
  accent2: Rgb;
  ink: Rgb;
  dark: boolean;
}

interface Pointer {
  x: number;
  y: number;
  /** 화면 위에 있는지(마우스가 나가면 false, 손가락은 누른 동안만) */
  inside: boolean;
  down: boolean;
  /** 이번 프레임까지 끈 거리(그리는 쪽이 읽고 비운다) */
  dragX: number;
  dragY: number;
}

interface Ripple {
  x: number;
  y: number;
  t: number;
}

interface Scene {
  resize: (w: number, h: number) => void;
  tap: (x: number, y: number) => void;
  /** dt: 초 */
  frame: (ctx: CanvasRenderingContext2D, dt: number, time: number) => void;
}

const ACCENT2: Rgb = [91, 140, 255];

function parseColor(value: string, fallback: Rgb): Rgb {
  const v = value.trim();
  const hex = /^#([0-9a-f]{6})$/i.exec(v)?.[1];
  if (hex) {
    return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
  }
  const rgb = /rgba?\(([^)]+)\)/.exec(v)?.[1];
  if (rgb) {
    const [r, g, b] = rgb.split(',').map((s) => parseFloat(s));
    return [r, g, b];
  }
  return fallback;
}

function readPalette(el: Element): Palette {
  const style = getComputedStyle(el);
  const accent = parseColor(style.getPropertyValue('--hs-accent'), [143, 130, 255]);
  const ink = parseColor(style.getPropertyValue('--hs-text-3'), [110, 110, 122]);
  const bg = parseColor(style.getPropertyValue('--hs-bg'), [17, 17, 20]);
  return { accent, accent2: ACCENT2, ink, dark: bg[0] + bg[1] + bg[2] < 384 };
}

const rgba = ([r, g, b]: Rgb, a: number) =>
  `rgba(${r | 0},${g | 0},${b | 0},${Math.max(0, Math.min(1, a)).toFixed(3)})`;
const mix = (a: Rgb, b: Rgb, t: number): Rgb => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];

function drawRipples(ctx: CanvasRenderingContext2D, ripples: Ripple[], palette: Palette, dt: number) {
  for (let i = ripples.length - 1; i >= 0; i--) {
    const r = ripples[i];
    r.t += dt;
    if (r.t > 1.2) {
      ripples.splice(i, 1);
      continue;
    }
    const k = r.t / 1.2;
    ctx.beginPath();
    ctx.arc(r.x, r.y, 12 + (1 - (1 - k) ** 3) * 220, 0, Math.PI * 2);
    ctx.strokeStyle = rgba(palette.accent, (1 - k) * 0.55);
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
}

function createGlobe(pointer: Pointer, palette: () => Palette, subtle: boolean, calm: boolean): Scene {
  let w = 0;
  let h = 0;
  let cx = 0;
  let cy = 0;
  let radius = 0;
  let points: [number, number, number][] = [];
  let edges: [number, number][] = [];
  let yaw = 0;
  let pitch = 0.38;
  const baseSpeed = calm ? 0 : 0.12;
  let yawSpeed = baseSpeed;
  let pitchSpeed = 0;
  // 누른 곳에서 구 표면을 따라 퍼지는 파동(시작점 단위 벡터, 경과 시간)
  const waves: { p: [number, number, number]; t: number }[] = [];
  const ripples: Ripple[] = [];

  // 화면에 그려진 위치(누른 곳에서 가장 가까운 점을 찾을 때 쓴다)
  let projected: { x: number; y: number; z: number }[] = [];

  const build = (n: number) => {
    // 피보나치 구: 점을 구 위에 고르게 놓는다
    points = [];
    const golden = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < n; i++) {
      const y = 1 - (i / (n - 1)) * 2;
      const r = Math.sqrt(1 - y * y);
      const a = golden * i;
      points.push([Math.cos(a) * r, y, Math.sin(a) * r]);
    }
    // 가장 가까운 이웃 3개를 이어 그물을 만든다
    const set = new Set<string>();
    edges = [];
    points.forEach((p, i) => {
      const near = points
        .map((q, j) => ({ j, d: p[0] * q[0] + p[1] * q[1] + p[2] * q[2] }))
        .filter((v) => v.j !== i)
        .sort((a, b) => b.d - a.d)
        .slice(0, 3);
      near.forEach(({ j }) => {
        const key = i < j ? `${i}-${j}` : `${j}-${i}`;
        if (!set.has(key)) {
          set.add(key);
          edges.push([i, j]);
        }
      });
    });
  };

  return {
    resize(nw, nh) {
      const first = w === 0;
      w = nw;
      h = nh;
      cx = w / 2;
      cy = h / 2;
      // 휴대폰은 폭보다 조금 크게 그려 가운데 카드 위아래로 지구본이 보이게 한다
      radius = w < 640 ? w * 0.62 : Math.min(w, h) * 0.44;
      const n = subtle ? 120 : w < 640 ? 160 : 230;
      if (first || points.length !== n) {
        build(n);
      }
    },

    tap(x, y) {
      ripples.push({ x, y, t: 0 });
      // 지구본 앞면을 눌렀으면 가장 가까운 점에서 파동을 일으킨다
      let best = -1;
      let bestD = Infinity;
      projected.forEach((p, i) => {
        if (p.z < 0) {
          return;
        }
        const d = (p.x - x) ** 2 + (p.y - y) ** 2;
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      });
      if (best >= 0 && bestD < (radius * 0.3) ** 2) {
        waves.push({ p: points[best], t: 0 });
      }
    },

    frame(ctx, dt) {
      const pal = palette();

      // 끌면 그만큼 돌리고, 놓으면 그 속도로 계속 돌다가(관성) 원래 속도로 돌아온다
      if (pointer.down && (pointer.dragX || pointer.dragY)) {
        const k = 1 / Math.max(radius, 1);
        yaw += pointer.dragX * k;
        pitch += pointer.dragY * k;
        yawSpeed = (pointer.dragX * k) / Math.max(dt, 1 / 120);
        pitchSpeed = (pointer.dragY * k) / Math.max(dt, 1 / 120);
        pointer.dragX = 0;
        pointer.dragY = 0;
      } else if (!pointer.down) {
        const ease = 1 - Math.exp(-dt * 1.6);
        yawSpeed += (baseSpeed - yawSpeed) * ease;
        pitchSpeed += (0 - pitchSpeed) * ease;
        yaw += yawSpeed * dt;
        pitch += pitchSpeed * dt;
      }
      yawSpeed = Math.max(-6, Math.min(6, yawSpeed));
      pitch = Math.max(-1.3, Math.min(1.3, pitch));

      const cosY = Math.cos(yaw);
      const sinY = Math.sin(yaw);
      const cosP = Math.cos(pitch);
      const sinP = Math.sin(pitch);
      const f = 2.8;

      waves.forEach((v) => (v.t += dt));
      while (waves.length && waves[0].t > 2.4) {
        waves.shift();
      }

      projected = points.map(([x, y, z]) => {
        const x1 = x * cosY + z * sinY;
        const z1 = -x * sinY + z * cosY;
        const y2 = y * cosP - z1 * sinP;
        const z2 = y * sinP + z1 * cosP;
        const s = f / (f - z2);
        return { x: cx + x1 * radius * s, y: cy + y2 * radius * s, z: z2 };
      });

      // 점마다 밝기: 깊이 + 파동 + 손가락 근처
      const glow = points.map((p, i) => {
        let g = 0;
        waves.forEach((v) => {
          const angle = Math.acos(Math.max(-1, Math.min(1, p[0] * v.p[0] + p[1] * v.p[1] + p[2] * v.p[2])));
          const front = v.t * 1.6;
          g += Math.exp(-(((angle - front) / 0.18) ** 2)) * (1 - v.t / 2.4);
        });
        if (pointer.inside) {
          const q = projected[i];
          const d = Math.hypot(q.x - pointer.x, q.y - pointer.y);
          if (q.z > -0.2 && d < 140) {
            g += (1 - d / 140) * 0.9;
          }
        }
        return Math.min(g, 1.4);
      });

      const strength = subtle ? 0.55 : 1;
      const lineBase = pal.dark ? 0.32 : 0.26;

      ctx.lineWidth = 1;
      edges.forEach(([a, b]) => {
        const p = projected[a];
        const q = projected[b];
        const depth = (p.z + q.z + 2) / 4;
        const g = (glow[a] + glow[b]) / 2;
        const alpha = (0.04 + depth * depth * lineBase + g * 0.5) * strength;
        if (alpha < 0.02) {
          return;
        }
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(q.x, q.y);
        ctx.strokeStyle = rgba(mix(pal.accent, pal.accent2, (p.x - cx + radius) / (2 * radius)), alpha);
        ctx.stroke();
      });

      projected.forEach((p, i) => {
        const depth = (p.z + 1) / 2;
        const g = glow[i];
        const r = 0.8 + depth * 1.6 + g * 2.2;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fillStyle = rgba(
          mix(pal.accent, pal.accent2, (p.x - cx + radius) / (2 * radius)),
          (0.12 + depth * 0.7 + g) * strength,
        );
        ctx.fill();
      });

      // 손가락·마우스에서 가까운 앞면 점으로 실을 잇는다
      if (pointer.inside) {
        projected.forEach((p) => {
          const d = Math.hypot(p.x - pointer.x, p.y - pointer.y);
          if (p.z > 0 && d < 120) {
            ctx.beginPath();
            ctx.moveTo(pointer.x, pointer.y);
            ctx.lineTo(p.x, p.y);
            ctx.strokeStyle = rgba(pal.accent, (1 - d / 120) * 0.45 * strength);
            ctx.stroke();
          }
        });
      }

      drawRipples(ctx, ripples, pal, dt);
    },
  };
}

interface Dot {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** 원래 떠다니던 속도. 밀리거나 끌려간 뒤 이 속도로 돌아온다 */
  bx: number;
  by: number;
}

function createNet(pointer: Pointer, palette: () => Palette, subtle: boolean, calm: boolean): Scene {
  let w = 0;
  let h = 0;
  let dots: Dot[] = [];
  let link = 130;
  const ripples: Ripple[] = [];

  const spawn = (): Dot => {
    const angle = Math.random() * Math.PI * 2;
    const speed = calm ? 0 : 8 + Math.random() * 16;
    const bx = Math.cos(angle) * speed;
    const by = Math.sin(angle) * speed;
    return { x: Math.random() * w, y: Math.random() * h, vx: bx, vy: by, bx, by };
  };

  return {
    resize(nw, nh) {
      w = nw;
      h = nh;
      link = w < 640 ? 110 : 140;
      const n = Math.round(Math.max(28, Math.min(subtle ? 70 : 130, (w * h) / (subtle ? 15000 : 9000))));
      while (dots.length < n) {
        dots.push(spawn());
      }
      dots = dots.slice(0, n);
      dots.forEach((d) => {
        d.x = Math.min(d.x, w);
        d.y = Math.min(d.y, h);
      });
    },

    tap(x, y) {
      ripples.push({ x, y, t: 0 });
      // 누른 곳에서 바깥으로 밀어낸다
      dots.forEach((d) => {
        const dx = d.x - x;
        const dy = d.y - y;
        const dist = Math.hypot(dx, dy) || 1;
        if (dist < 260) {
          const push = (1 - dist / 260) * 420;
          d.vx += (dx / dist) * push;
          d.vy += (dy / dist) * push;
        }
      });
    },

    frame(ctx, dt) {
      const pal = palette();
      const ease = 1 - Math.exp(-dt * 1.2);
      const reach = pointer.down ? 240 : 170;

      dots.forEach((d) => {
        // 손가락·마우스 쪽으로 모인다. 누르고 끌면 더 세게 끌려온다
        if (pointer.inside) {
          const dx = pointer.x - d.x;
          const dy = pointer.y - d.y;
          const dist = Math.hypot(dx, dy) || 1;
          if (dist < reach && dist > 24) {
            const pull = (1 - dist / reach) * (pointer.down ? 520 : 140);
            d.vx += (dx / dist) * pull * dt;
            d.vy += (dy / dist) * pull * dt;
          }
        }
        d.vx += (d.bx - d.vx) * ease;
        d.vy += (d.by - d.vy) * ease;
        d.x += d.vx * dt;
        d.y += d.vy * dt;

        // 화면 밖으로 나가면 반대편에서 다시 들어온다
        const m = 20;
        if (d.x < -m) d.x = w + m;
        if (d.x > w + m) d.x = -m;
        if (d.y < -m) d.y = h + m;
        if (d.y > h + m) d.y = -m;
      });
      pointer.dragX = 0;
      pointer.dragY = 0;

      const strength = subtle ? 0.6 : 1;
      const lineBase = pal.dark ? 0.42 : 0.34;

      // 선은 밝기를 몇 단계로 묶어 한 번에 그린다(선마다 stroke하면 느리다)
      const buckets: number[][] = Array.from({ length: 6 }, () => []);
      for (let i = 0; i < dots.length; i++) {
        const a = dots[i];
        for (let j = i + 1; j < dots.length; j++) {
          const b = dots[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          if (Math.abs(dx) > link || Math.abs(dy) > link) {
            continue;
          }
          const d = Math.hypot(dx, dy);
          if (d < link) {
            const k = Math.min(5, Math.floor((1 - d / link) * 6));
            buckets[k].push(a.x, a.y, b.x, b.y);
          }
        }
      }
      ctx.lineWidth = 1;
      buckets.forEach((segs, k) => {
        if (!segs.length) {
          return;
        }
        ctx.beginPath();
        for (let i = 0; i < segs.length; i += 4) {
          ctx.moveTo(segs[i], segs[i + 1]);
          ctx.lineTo(segs[i + 2], segs[i + 3]);
        }
        ctx.strokeStyle = rgba(mix(pal.ink, pal.accent, 0.55), ((k + 1) / 6) * lineBase * strength);
        ctx.stroke();
      });

      if (pointer.inside) {
        dots.forEach((d) => {
          const dist = Math.hypot(d.x - pointer.x, d.y - pointer.y);
          if (dist < reach) {
            ctx.beginPath();
            ctx.moveTo(pointer.x, pointer.y);
            ctx.lineTo(d.x, d.y);
            ctx.strokeStyle = rgba(pal.accent, (1 - dist / reach) * 0.6 * strength);
            ctx.stroke();
          }
        });
      }

      dots.forEach((d) => {
        const near = pointer.inside ? Math.max(0, 1 - Math.hypot(d.x - pointer.x, d.y - pointer.y) / reach) : 0;
        ctx.beginPath();
        ctx.arc(d.x, d.y, 1.6 + near * 1.8, 0, Math.PI * 2);
        ctx.fillStyle = rgba(mix(pal.accent, pal.accent2, d.x / Math.max(w, 1)), (0.55 + near * 0.45) * strength);
        ctx.fill();
      });

      drawRipples(ctx, ripples, pal, dt);
    },
  };
}

/**
 * 화면 뒤에 깔리는 움직이는 기하학 배경(로그인·회원가입·안내 화면). 옛 vanta(three.js) 배경을 대신한다.
 * 색은 hs 토큰(--hs-accent 등)을 읽어 라이트/다크 테마를 따른다. 빈 곳을 끌거나 누르면 반응한다.
 * 위에 올리는 내용(`HsCenterPage`)은 빈 곳의 손가락을 이 캔버스로 흘려보낸다.
 */
export default function HsBackdrop({ variant, subtle = false, className }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) {
      return;
    }

    let palette = readPalette(canvas);
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const pointer: Pointer = { x: 0, y: 0, inside: false, down: false, dragX: 0, dragY: 0 };
    const scene = (variant === 'globe' ? createGlobe : createNet)(pointer, () => palette, subtle, calm);

    let w = 0;
    let h = 0;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = rect.width;
      h = rect.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      scene.resize(w, h);
    };
    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);

    // 테마를 바꾸면(계정 메뉴·기기 설정) 색을 다시 읽는다
    const refreshPalette = () => (palette = readPalette(canvas));
    const themeObserver = new MutationObserver(refreshPalette);
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', refreshPalette);

    const local = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const onDown = (e: PointerEvent) => {
      const p = local(e);
      Object.assign(pointer, p, { inside: true, down: true, dragX: 0, dragY: 0 });
      canvas.setPointerCapture?.(e.pointerId);
      scene.tap(p.x, p.y);
    };
    const onMove = (e: PointerEvent) => {
      const p = local(e);
      if (pointer.down) {
        pointer.dragX += p.x - pointer.x;
        pointer.dragY += p.y - pointer.y;
      }
      Object.assign(pointer, p, { inside: e.pointerType === 'mouse' || pointer.down });
    };
    const onUp = (e: PointerEvent) => {
      pointer.down = false;
      // 손가락은 떼면 사라진다. 마우스는 계속 위에 있다
      if (e.pointerType !== 'mouse') {
        pointer.inside = false;
      }
    };
    const onLeave = (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && !pointer.down) {
        pointer.inside = false;
      }
    };
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);
    canvas.addEventListener('pointerleave', onLeave);

    let raf = 0;
    let last = performance.now();
    const loop = (now: number) => {
      // 탭을 오래 비웠다 돌아오면 한 번에 크게 움직이지 않게 끊는다
      const dt = Math.min((now - last) / 1000, 1 / 20);
      last = now;
      ctx.clearRect(0, 0, w, h);
      scene.frame(ctx, dt, now / 1000);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      themeObserver.disconnect();
      media.removeEventListener('change', refreshPalette);
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
      canvas.removeEventListener('pointerleave', onLeave);
    };
  }, [variant, subtle]);

  return <canvas ref={ref} className={cs('HsBackdrop', className)} aria-hidden />;
}
